import { useCallback } from 'react'

import { useAppSelector } from '@editor/store/app'

import { selectUnlabeledColor } from '../store/selectors'

/**
 * Resolves the unclassified-element color for the area a shape belongs to.
 *
 * The loaded display setting describes the area currently open, so a view that
 * draws several areas at once — the blueprint editor, which renders every
 * connected area's 3D elements — has to read each *other* area's own
 * `display_config` for its elements to match what that area's 3D editor shows.
 *
 * The area currently open keeps using the loaded setting on purpose: that is the
 * live value the settings drawer and the elements panel colour picker write to,
 * whereas an area's `display_config` only catches up once a save comes back.
 *
 * @returns A resolver taking the area an element belongs to, defaulting to the
 * loaded setting whenever that area has no colour of its own.
 */
const useUnlabeledColor = () => {
  const unlabeledColor = useAppSelector(selectUnlabeledColor)
  const currentAreaId = useAppSelector((state) => state.page.inspectionArea?.inspection_area_id)
  const inspectionAreas = useAppSelector((state) => state.page.inspectionAreas)

  return useCallback(
    (areaId?: string) => {
      if (!areaId || areaId === currentAreaId) return unlabeledColor

      const area = inspectionAreas.find((candidate) => candidate.inspection_area_id === areaId)
      return area?.display_config?.general.unlabeled_color || unlabeledColor
    },
    [unlabeledColor, currentAreaId, inspectionAreas],
  )
}

export default useUnlabeledColor
